import { useCallback, useEffect, useState } from 'react';

import {
  fetchStaffPayRates,
  PayRateRequestError,
  removeStaffPayRate,
  setStaffPayRate,
  setStaffSalaried,
  type PayRateRole,
  type SetPayRateInput,
  type StaffPayRates,
} from '../../../api/staff-pay-rates';

import { payRateErrorMessage, shouldReload, type PayRateAction } from './pay-rate-error-message';

export type WriteResult = { ok: true } | { ok: false; fieldErrors: Record<string, string> };

/** Where a failed action's message belongs: beside one role, or the salaried switch. */
export type ErrorScope = PayRateRole | 'salaried';

export interface ActionError {
  scope: ErrorScope;
  message: string;
}

/** Loads one person's pay rates and runs the three writes. Every write replaces
 *  the state with the rates the API returns. */
export function useStaffPayRates(userId: string) {
  const [payRates, setPayRates] = useState<StaffPayRates | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadFailure, setLoadFailure] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState<ActionError | null>(null);

  const load = useCallback(
    async (isStale: () => boolean = () => false) => {
      setLoading(true);
      setLoadFailure(null);
      try {
        const loaded = await fetchStaffPayRates(userId);
        if (!isStale()) setPayRates(loaded);
      } catch (err) {
        if (!isStale()) setLoadFailure(err);
      } finally {
        if (!isStale()) setLoading(false);
      }
    },
    [userId],
  );

  useEffect(() => {
    let stale = false;
    void load(() => stale);
    return () => {
      stale = true;
    };
  }, [load]);

  const write = useCallback(
    async (
      call: () => Promise<StaffPayRates>,
      scope: ErrorScope,
      action: PayRateAction,
    ): Promise<WriteResult> => {
      setBusy(true);
      setActionError(null);
      try {
        setPayRates(await call());
        return { ok: true };
      } catch (err) {
        if (
          action === 'set' &&
          err instanceof PayRateRequestError &&
          err.status === 400 &&
          Object.keys(err.fieldErrors).length > 0
        ) {
          return { ok: false, fieldErrors: err.fieldErrors };
        }
        if (shouldReload(err, action)) {
          void fetchStaffPayRates(userId).then(setPayRates, () => undefined);
        }
        setActionError({ scope, message: payRateErrorMessage(err, action) });
        return { ok: false, fieldErrors: {} };
      } finally {
        setBusy(false);
      }
    },
    [userId],
  );

  return {
    payRates,
    loading,
    loadError: loadFailure ? payRateErrorMessage(loadFailure, 'load') : null,
    hidden: loadFailure instanceof PayRateRequestError && loadFailure.status === 403,
    busy,
    actionError,
    retry: () => void load(),
    setRate: (input: SetPayRateInput) =>
      write(() => setStaffPayRate(userId, input), input.role, 'set'),
    removeRate: (role: PayRateRole, rateId: string) =>
      write(() => removeStaffPayRate(userId, rateId), role, 'remove'),
    setSalaried: (isSalaried: boolean) =>
      write(() => setStaffSalaried(userId, isSalaried), 'salaried', 'salaried'),
  };
}
